/* OPQ Archive — hover a title to spawn its featured photo onto the canvas
   at a designer-picked position. Photos can be dragged; final positions
   are persisted in localStorage so the page remembers user arrangement. */

const STORAGE_KEY = 'opq-archive-positions';
const STORAGE_KEY_BG = 'opq-archive-bg';

/* Designer-picked positions per article handle.  Coords are normalised to
   the canvas dimensions (0–1).  Rotation in degrees.  Keep slots spread
   so photos minimally overlap.

   Add new entries here when articles are published; unmapped handles fall
   back to a deterministic position derived from the index. */
/* Shopify prefixes article handles with the blog handle (`archive/...`).
   Keep both the prefixed and bare keys so this works whether the blog
   handle changes in the future. */
const POSITIONS = {
  'archive/beyblade-watch-x-13co-beyblade-watch':  { x: 0.30, y: 0.06, rotation: -4 },
  'archive/beyblade-watch-pop-up-event':           { x: 0.78, y: 0.14, rotation: 6 },
  'archive/dajah-sound-system':                    { x: 0.22, y: 0.30, rotation: -8 },
  'archive/furniture-for-taipei-music-center':     { x: 0.62, y: 0.40, rotation: 3 },
  'archive/4-meters-speaker':                      { x: 0.38, y: 0.58, rotation: -5 },
  'archive/auctor':                                { x: 0.74, y: 0.72, rotation: 9 },
};

/* Fallback positions for articles without an explicit slot.  A tossed
   diagonal staircase that stays roughly in the canvas bounds. */
const FALLBACK_SLOTS = [
  { x: 0.45, y: 0.20, rotation: -6 },
  { x: 0.68, y: 0.30, rotation: 4 },
  { x: 0.18, y: 0.45, rotation: -3 },
  { x: 0.55, y: 0.55, rotation: 7 },
  { x: 0.82, y: 0.65, rotation: -5 },
  { x: 0.32, y: 0.80, rotation: 8 },
  { x: 0.65, y: 0.88, rotation: -2 },
];

const PHOTO_SAFE_MARGIN = 32;
const PHOTO_MIN_VISIBLE = 96;

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function readStorage() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
}

function writeStorage(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); }
  catch { /* localStorage may be disabled — fail silently */ }
}

function initArchive(root) {
  const dataNode = root.querySelector('[data-opq-archive-data]');
  const titlesEl = root.querySelector('[data-opq-archive-titles]');
  const canvas   = root.querySelector('[data-opq-archive-canvas]');
  if (!dataNode || !titlesEl || !canvas) return;

  /* Skip the desktop interaction layer on touch / narrow viewports — the
     mobile <ul> list takes over via CSS. */
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  if (isMobile) return;

  const articles = Array.from(dataNode.querySelectorAll('article')).map((node, idx) => ({
    handle:  node.dataset.handle,
    title:   node.dataset.title,
    url:     node.dataset.url,
    image:   node.dataset.image,
    imageW:  parseInt(node.dataset.imageW, 10) || 0,
    imageH:  parseInt(node.dataset.imageH, 10) || 0,
    index:   idx,
  }));

  /* Pre-load all featured images on page load so the first hover is
     instant (no network round-trip). */
  articles.forEach((article) => {
    if (!article.image) return;
    const img = new Image();
    img.src = article.image;
  });

  const photoMap = new Map();             // handle → photo element
  const positions = readStorage();
  let zCounter = 1;
  let focusedHandle = null;

  const positionFor = (article) => {
    if (POSITIONS[article.handle]) return POSITIONS[article.handle];
    return FALLBACK_SLOTS[article.index % FALLBACK_SLOTS.length];
  };

  const placePhoto = (photo, x01, y01, rotation) => {
    /* Position photo so its centre lands on the (x, y) point in the canvas */
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const pw = photo.offsetWidth || 320;
    const ph = photo.offsetHeight || 240;
    const rawLeft = (cw * x01) - pw / 2;
    const rawTop  = (ch * y01) - ph / 2;
    const left = clamp(rawLeft, 0, Math.max(0, cw - pw));
    const top  = clamp(rawTop,  0, Math.max(0, ch - ph));
    photo.style.left = `${left}px`;
    photo.style.top  = `${top}px`;
    photo.style.transform = `rotate(${rotation}deg)`;
  };

  const clampDragPosition = (photo, left, top) => {
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const pw = photo.offsetWidth || PHOTO_MIN_VISIBLE;
    const ph = photo.offsetHeight || PHOTO_MIN_VISIBLE;
    return {
      left: clamp(left, PHOTO_MIN_VISIBLE - pw, cw - PHOTO_MIN_VISIBLE),
      top: clamp(top, PHOTO_MIN_VISIBLE - ph, ch - PHOTO_MIN_VISIBLE),
    };
  };

  const focusScaleFor = (photo) => {
    const rect = photo.getBoundingClientRect();
    const maxW = window.innerWidth * 0.72;
    const maxH = window.innerHeight * 0.78;
    const byWidth = maxW / Math.max(rect.width, 1);
    const byHeight = maxH / Math.max(rect.height, 1);
    return clamp(Math.min(2, byWidth, byHeight), 1, 2);
  };

  const viewportShiftFor = (rect, scale) => {
    const cx = rect.left + rect.width / 2;
    const scaledW = rect.width * scale;
    let left = cx - scaledW / 2;
    let right = cx + scaledW / 2;
    let dx = 0;

    if (left < PHOTO_SAFE_MARGIN) dx = PHOTO_SAFE_MARGIN - left;
    else if (right > window.innerWidth - PHOTO_SAFE_MARGIN) {
      dx = window.innerWidth - PHOTO_SAFE_MARGIN - right;
    }

    return { dx };
  };

  const persistPhoto = (photo) => {
    const handle = photo.dataset.handle;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    const left = parseFloat(photo.style.left) || 0;
    const top  = parseFloat(photo.style.top)  || 0;
    const pw = photo.offsetWidth;
    const ph = photo.offsetHeight;
    const xCentre = left + pw / 2;
    const yCentre = top  + ph / 2;
    /* Record normalised coords so position stays sane across viewport
       resizes. */
    positions[handle] = {
      x: xCentre / cw,
      y: yCentre / ch,
      rotation: parseFloat(photo.dataset.rotation) || 0,
      z: parseInt(photo.style.zIndex, 10) || 1,
    };
    writeStorage(positions);
  };

  const bringToFront = (photo) => {
    zCounter += 1;
    photo.style.zIndex = String(zCounter);
  };

  const enableDrag = (photo) => {
    let pointerId = null;
    let offsetX = 0;
    let offsetY = 0;

    const onDown = (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      /* Drop any active focus state so transform = rotate only.  Otherwise
         the photo would still be scale(2) while we're trying to drag it. */
      unfocusAll();
      pointerId = e.pointerId;
      photo.setPointerCapture(pointerId);
      const rect = photo.getBoundingClientRect();
      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;
      photo.classList.add('is-dragging');
      bringToFront(photo);
      e.preventDefault();
    };

    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      const canvasRect = canvas.getBoundingClientRect();
      const rawX = e.clientX - canvasRect.left - offsetX;
      const rawY = e.clientY - canvasRect.top  - offsetY;
      const next = clampDragPosition(photo, rawX, rawY);
      photo.style.left = `${next.left}px`;
      photo.style.top  = `${next.top}px`;
    };

    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      photo.releasePointerCapture(pointerId);
      pointerId = null;
      photo.classList.remove('is-dragging');
      persistPhoto(photo);
    };

    photo.addEventListener('pointerdown', onDown);
    photo.addEventListener('pointermove', onMove);
    photo.addEventListener('pointerup', onUp);
    photo.addEventListener('pointercancel', onUp);
  };

  const spawnPhoto = (article) => {
    if (photoMap.has(article.handle)) return photoMap.get(article.handle);

    const photo = document.createElement('div');
    photo.className = 'opq-archive-photo';
    photo.dataset.handle = article.handle;

    const img = document.createElement('img');
    img.src = article.image;
    img.alt = article.title;
    img.draggable = false;
    img.loading = 'eager';
    /* Setting width / height attrs on the img makes layout reserve space
       BEFORE the image bytes finish loading, so offsetHeight is correct
       on the very first paint (no jump on load). */
    if (article.imageW && article.imageH) {
      img.width  = article.imageW;
      img.height = article.imageH;
    }
    photo.appendChild(img);

    /* Determine final placement: saved drag → designer slot → fallback. */
    const saved = positions[article.handle];
    const slot = positionFor(article);
    const x = saved ? saved.x : slot.x;
    const y = saved ? saved.y : slot.y;
    const rotation = saved && saved.rotation !== undefined
      ? saved.rotation
      : slot.rotation;
    photo.dataset.rotation = String(rotation);

    canvas.appendChild(photo);

    /* Force a synchronous layout so offsetWidth / offsetHeight are
       populated, then place the photo immediately. */
    photo.getBoundingClientRect();
    placePhoto(photo, x, y, rotation);

    /* Reveal next frame so the opacity transition can run.  Image is
       already cached (preloaded above), so no flash of empty box. */
    requestAnimationFrame(() => {
      photo.classList.add('is-spawned');
    });

    bringToFront(photo);
    enableDrag(photo);

    photoMap.set(article.handle, photo);
    return photo;
  };

  /* Apply rest transform (just rotation) — used after unfocus or to
     initialise.  Photos retain their drag-saved left/top; transform
     stays purely as rotation. */
  const setRestTransform = (photo) => {
    const rot = parseFloat(photo.dataset.rotation) || 0;
    photo.style.transform = `rotate(${rot}deg)`;
  };

  const focusPhoto = (handle) => {
    const focused = photoMap.get(handle);
    if (!focused) return;
    focusedHandle = handle;
    bringToFront(focused);

    const fr = focused.getBoundingClientRect();
    const fcx = fr.left + fr.width / 2;
    const fcy = fr.top + fr.height / 2;
    const fRot = parseFloat(focused.dataset.rotation) || 0;

    const scale = focusScaleFor(focused);
    const shift = viewportShiftFor(fr, scale);

    /* Focused: enlarged, slightly straightened so the upscale is legible.
       Extra translate keeps the enlarged photo inside the visible viewport
       without changing the user's saved arrangement. */
    focused.classList.add('is-focused');
    focused.style.transform =
      `translate(${shift.dx.toFixed(1)}px, 0) rotate(${fRot * 0.25}deg) scale(${scale.toFixed(3)})`;

    /* Push every other visible photo away from the focused centre. */
    const PUSH = 280;
    photoMap.forEach((photo, h) => {
      if (h === handle) return;
      const r = photo.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = cx - fcx;
      const dy = cy - fcy;
      const dist = Math.max(Math.hypot(dx, dy), 1);
      const px = (dx / dist) * PUSH;
      const py = (dy / dist) * PUSH;
      const rot = parseFloat(photo.dataset.rotation) || 0;
      photo.style.transform =
        `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) rotate(${rot}deg) scale(0.86)`;
    });

    /* Smooth-scroll so the focused photo's centre lands at viewport centre.
       Using offsetTop (transform-independent) so the target stays correct
       even mid-scale animation. */
    const canvasRect = canvas.getBoundingClientRect();
    const photoCenterY = canvasRect.top + window.scrollY
                       + focused.offsetTop + focused.offsetHeight / 2;
    const targetTop = Math.max(0, photoCenterY - window.innerHeight / 2);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  };

  const unfocusAll = () => {
    if (focusedHandle === null) return;
    focusedHandle = null;
    photoMap.forEach((photo) => {
      photo.classList.remove('is-focused');
      setRestTransform(photo);
    });
  };

  /* Title hover:
       1. Spawn the matching photo if not yet (subsequent hovers no-op the spawn)
       2. Focus it (scale 2x) + push the others away
     Title pointerleave: unfocus everything (photos return to designer slots /
     drag-saved positions). */
  articles.forEach((article) => {
    const titleEl = titlesEl.querySelector(`[data-opq-archive-title="${article.handle}"]`);
    if (!titleEl) return;

    titleEl.addEventListener('pointerenter', () => {
      if (!photoMap.has(article.handle)) {
        spawnPhoto(article);
        /* Wait one frame so the rest transform is committed, then focus.
           Otherwise the focus push computes geometry while photo still
           has opacity:0 and transform: none. */
        requestAnimationFrame(() => focusPhoto(article.handle));
      } else {
        focusPhoto(article.handle);
      }
    });
    titleEl.addEventListener('pointerleave', () => {
      unfocusAll();
    });
  });

  /* Re-position un-dragged photos on resize so they stay in their normalised
     slots.  Photos the user has moved keep their saved coords. */
  const reflow = () => {
    photoMap.forEach((photo) => {
      const handle = photo.dataset.handle;
      const saved = positions[handle];
      const article = articles.find((a) => a.handle === handle);
      if (!article) return;
      const slot = positionFor(article);
      const x = saved ? saved.x : slot.x;
      const y = saved ? saved.y : slot.y;
      const rotation = saved && saved.rotation !== undefined
        ? saved.rotation
        : slot.rotation;
      placePhoto(photo, x, y, rotation);
    });
  };
  let resizeRaf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(reflow);
  }, { passive: true });

  /* === Background colour picker (bottom-left) ===
     Persists user choice in localStorage so reload restores it. */
  const bgBtn = root.querySelector('[data-opq-archive-bg-btn]');
  const bgIn  = root.querySelector('[data-opq-archive-bg-input]');
  const applyBg = (color) => {
    if (!color) return;
    document.documentElement.style.setProperty('--opq-archive-bg', color);
    if (bgIn) bgIn.value = color;
  };
  applyBg(localStorage.getItem(STORAGE_KEY_BG) || '#ffffff');
  bgBtn?.addEventListener('click', () => bgIn?.click());
  bgIn?.addEventListener('input', (e) => {
    applyBg(e.target.value);
    try { localStorage.setItem(STORAGE_KEY_BG, e.target.value); } catch {}
  });

  /* === Cursor-following negative-film spotlight ===
     A position:fixed white circle with mix-blend-mode: difference inverts
     whatever it's over.  Follows the pointer; fades in on first move and
     out when the pointer leaves the window. */
  const cursorMask = root.querySelector('[data-opq-archive-cursor]');
  if (cursorMask) {
    let maskRaf = 0;
    let lastX = 0, lastY = 0;
    const updateMask = () => {
      maskRaf = 0;
      cursorMask.style.transform = `translate(${lastX}px, ${lastY}px) translate(-50%, -50%)`;
    };
    window.addEventListener('pointermove', (e) => {
      lastX = e.clientX;
      lastY = e.clientY;
      cursorMask.classList.add('is-active');
      if (!maskRaf) maskRaf = requestAnimationFrame(updateMask);
    }, { passive: true });
    window.addEventListener('pointerleave', () => {
      cursorMask.classList.remove('is-active');
    });
    document.addEventListener('mouseleave', () => {
      cursorMask.classList.remove('is-active');
    });
  }
}

document.querySelectorAll('[data-opq-archive]').forEach(initArchive);
